import { BodyText, toast } from "@/components/ui";
import { EditableRecordButton } from "@/features/Patient/MedicalRecords/components";
import { useIsMutating } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import type * as fhirTypes from "fhir/r4";
import { useState } from "react";
import type { z } from "zod";

import { QueryKeys } from "@/constants/Query";
import { splitByLineBreaks } from "@/lib/formatters";
import { isActionAvailable } from "@/utils/available-actions";
import { useCarePlanSummary } from "../network/useCarePlanSummary";
import { CareplanAvailableAction } from "../network/useCarePlanSummary";
import { useCreateRecommendations } from "../network/useCreateRecommendations";
import { useDeleteRecommendation } from "../network/useDeleteRecommendation";
import { useUpdateRecommendation } from "../network/useUpdateRecommendation";
import { RecommendationEditor } from "./RecommendationEditor";
import type { editRecommendationSchema } from "./schema";

interface RecommendationProps {
  carePlanId: string;
  encounterId: string;
  task: fhirTypes.Task;
}

export function Recommendation({
  carePlanId,
  encounterId,
  task,
}: RecommendationProps) {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const [isEditing, setIsEditing] = useState(false);

  const { data: careplanSummary } = useCarePlanSummary(encounterId);
  const careplanAvailableActions = careplanSummary?.availableActions;

  const canWriteRecommendations = isActionAvailable(
    CareplanAvailableAction.ADD_RECOMMENDATION,
    careplanAvailableActions,
  );
  const canReadRecommendations = isActionAvailable(
    CareplanAvailableAction.READ_RECOMMENDATION,
    careplanAvailableActions,
  );

  if (!canReadRecommendations) {
    return null;
  }

  const updateRecommendation = useUpdateRecommendation({
    encounterId,
    carePlanId,
    recommendationId: task.id,
  });

  const deleteRecommendation = useDeleteRecommendation({
    encounterId,
    carePlanId,
    recommendationId: task.id,
  });

  // True while ANY single or bulk recommendation void is in flight for this
  // careplan. Used to block a single void from racing a bulk void (and vice
  // versa), which would cause a FHIR 409 lost-update conflict (EHR-8188).
  const isAnyVoidPending =
    useIsMutating({
      mutationKey:
        QueryKeys.Patient.Chart.EpisodesOfCare.CarePlan.Recommendation.VOID({
          patientId,
          encounterId,
          carePlanId,
        }),
    }) > 0;

  const createRecommendations = useCreateRecommendations({
    encounterId,
    carePlanId,
  });

  const onSubmit = async (values: z.infer<typeof editRecommendationSchema>) => {
    try {
      const recommendations = splitByLineBreaks(values.text);

      if (recommendations?.length === 1) {
        await updateRecommendation.mutateAsync({
          recommendationDescription: recommendations[0],
        });
      } else if (recommendations?.length > 1) {
        await updateRecommendation.mutateAsync({
          recommendationDescription: recommendations[0],
        });
        await createRecommendations.mutateAsync({
          addAfterTaskId: task.id,
          recommendationDescriptions: recommendations.slice(1),
        });
      }
      setIsEditing(false);
    } catch (error) {
      toast.error("Failed to update recommendation.");
    }
  };

  const onDelete = async () => {
    try {
      await deleteRecommendation.mutateAsync();
    } catch (error) {
      toast.error("Failed to delete recommendation.");
    }
  };

  if (isEditing) {
    return (
      <RecommendationEditor
        onSubmit={onSubmit}
        onCancel={() => setIsEditing(false)}
        isSaving={
          updateRecommendation.isPending || createRecommendations.isPending
        }
        task={task}
      />
    );
  }

  return (
    <EditableRecordButton
      onClick={() => setIsEditing(true)}
      onDelete={
        canWriteRecommendations && !isAnyVoidPending ? onDelete : undefined
      }
      disabled={!canWriteRecommendations}
      deletePending={deleteRecommendation.isPending}
      deleteTooltipText="Delete Recommendation"
    >
      <ul className="list-outside list-disc">
        <li className="leading-15 ml-2">
          <BodyText
            size="sm"
            textColor={canWriteRecommendations ? "primary" : "tertiary"}
            className="self-center"
            preserveLineBreaks
          >
            {task.description}
          </BodyText>
        </li>
      </ul>
    </EditableRecordButton>
  );
}
